import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Recalcula los precios de paquete.
 * Un item con `paquete_de` solo conserva su descuento mientras el
 * producto principal siga en el carrito; si se quita, vuelve a precio de lista.
 */
function recalcularPaquetes(lista) {
  const ids = new Set(lista.map(i => i.id));
  return lista.map(i => {
    const precioLista = r2(i.precio_lista ?? i.precio);
    const pct         = Number(i.descuento_pct) || 0;
    const aplica      = pct > 0 && i.paquete_de && ids.has(i.paquete_de);
    return {
      ...i,
      precio_lista:     precioLista,
      precio:           aplica ? r2(precioLista * (1 - pct / 100)) : precioLista,
      descuento_activo: aplica,
    };
  });
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('comunicar_carrito');
      return saved ? recalcularPaquetes(JSON.parse(saved)) : [];
    } catch {
      return [];
    }
  });
  const [ultimoAgregado, setUltimoAgregado] = useState(null);

  /* Persistir en localStorage */
  useEffect(() => {
    try {
      localStorage.setItem('comunicar_carrito', JSON.stringify(items));
    } catch {}
  }, [items]);

  /* Agregar item — evita duplicados */
  const agregarItem = useCallback((item) => {
    setItems(prev => {
      if (prev.find(i => i.id === item.id)) return prev;
      return recalcularPaquetes([...prev, { ...item, cantidad: 1 }]);
    });
    setUltimoAgregado(item);
  }, []);

  /* Quitar item por id — revierte descuentos que dependían de él */
  const quitarItem = useCallback((id) => {
    setItems(prev => recalcularPaquetes(prev.filter(i => i.id !== id)));
  }, []);

  /* Vaciar carrito */
  const vaciarCarrito = useCallback(() => {
    setItems([]);
  }, []);

  /* Limpiar ultimo agregado (para cerrar el toast) */
  const limpiarUltimoAgregado = useCallback(() => {
    setUltimoAgregado(null);
  }, []);

  /* Verificar si un producto ya está en el carrito */
  const estaEnCarrito = useCallback((id) => {
    return items.some(i => i.id === id);
  }, [items]);

  /* Totales */
  const totalItems  = items.length;
  const totalPrecio = r2(items.reduce((a, i) => a + i.precio * (i.cantidad || 1), 0));
  const totalLista  = r2(items.reduce((a, i) => a + (i.precio_lista ?? i.precio) * (i.cantidad || 1), 0));
  const ahorroPaquete = r2(totalLista - totalPrecio);

  return (
    <CartContext.Provider value={{
      items,
      agregarItem,
      quitarItem,
      vaciarCarrito,
      estaEnCarrito,
      limpiarUltimoAgregado,
      ultimoAgregado,
      totalItems,
      totalPrecio,
      totalLista,
      ahorroPaquete,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart debe usarse dentro de CartProvider');
  return ctx;
}